import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import {
  getBindings,
  getNotifications,
  getProfile,
  getSubscriptions,
  markAllRead,
  markRead,
} from './service';
import type { NotificationParams } from './types';

export const accountKeys = {
  root: (id?: string) => ['account', id] as const,
  profile: (id?: string) => ['account', id, 'profile'] as const,
  notifications: (id?: string) => ['account', id, 'notifications'] as const,
};
export function useProfile() {
  const id = useAuthStore((state) => state.currentUser?.id);
  return useQuery({
    queryKey: accountKeys.profile(id),
    queryFn: ({ signal }) => getProfile(signal),
    enabled: !!id,
  });
}
export function useBindings() {
  const id = useAuthStore((state) => state.currentUser?.id);
  return useQuery({
    queryKey: [...accountKeys.root(id), 'bindings'],
    queryFn: ({ signal }) => getBindings(signal),
    enabled: !!id,
  });
}
export function useSubscriptions() {
  const id = useAuthStore((state) => state.currentUser?.id);
  return useQuery({
    queryKey: [...accountKeys.root(id), 'subscriptions'],
    queryFn: ({ signal }) => getSubscriptions(signal),
    enabled: !!id,
  });
}
export function useNotifications(params: NotificationParams) {
  const id = useAuthStore((state) => state.currentUser?.id);
  return useQuery({
    queryKey: [...accountKeys.notifications(id), params],
    queryFn: ({ signal }) => getNotifications(params, signal),
    enabled: !!id,
    staleTime: 0,
  });
}
export function useReadNotification() {
  const id = useAuthStore((state) => state.currentUser?.id);
  const client = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string | null) =>
      notificationId === null ? markAllRead() : markRead(notificationId),
    // 用户隔离的查询根同时更新角标、预览和中心；不对失败请求提前减计数。
    onSuccess: () =>
      client.invalidateQueries({ queryKey: accountKeys.notifications(id) }),
  });
}
